import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { IMyDpOptions, IMyDateModel } from "mydatepicker";
import { Signatory, SignatoryError, SignatoryErrorType } from "../../Classes/Signatory";
import { StartDateError, StartDateErrorType, StartDateLockReason } from "../../Classes/StartDate";

type FormError = SignatoryError | StartDateError;

@Component({
  selector: "SignatoryForm",
  templateUrl: "./SignatoryForm.component.html",
  styleUrls: ['../../../Content/css/bootstrap.css', '../../../Content/css/custom.css', '../../../Content/css/master.css', '../../../Content/css/innflow-top-menu.css',
    '../../../Content/css/innflow-main.css', '../../../Content/css/jquery.dropdownReplacement.css',
    '../../../Content/css/jquery.dropdownReplacementhotel.css'],
})
export class SignatoryFormComponent implements OnInit {
  @Input() signatoryModel: Signatory = {
    signatoryId: undefined,
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    hotelId: -1,
    startDate: null
  };

  @Input() isEditable: boolean = true;

  public isStartDateEditable: boolean = true;
  public startDateLockReason: StartDateLockReason = 'None';
  public startDateLockTooltip: string = '';
  public startDateErrorMessage: string | null = null;

  // Original start date (ISO yyyy-MM-dd) reflecting the last loaded value. Public because
  // the parent (SubscriptionDetails) resets it after a successful save. Used to detect
  // changes and to revert on cancel.
  public originalStartDate: string | null = null;

  public totalEmployees: string = '';
  public startDateModel: IMyDateModel | null = null;
  public startDatePickerOptions: IMyDpOptions = (() => {
    const t = new Date();
    return {
      dateFormat: 'mm/dd/yyyy',
      height: '20px',
      width: '95%',
      selectionTxtFontSize: '12px',
      inline: false,
      selectorHeight: '218px',
      selectorWidth: '247px',
      openSelectorOnInputClick: true,
      openSelectorTopOfInput: true,
      editableDateField: false,
      disableUntil: { year: t.getFullYear(), month: t.getMonth() + 1, day: t.getDate() }
    };
  })();

  private errors: FormError[] = [];

  @Output('valueChanged') onSignatorySaved = new EventEmitter<Signatory>();
  @Output() onTotalEmployeesChange = new EventEmitter<string>();

  ngOnInit(): void {
    this.refreshStartDateModel();
  }

  public isValid() {
    this.errors = [];
    if (this.signatoryModel.firstName === '') {
      this.errors.push({ type: SignatoryErrorType.FirstNameEmpty, message: 'First Name is empty' })
    }
    if (this.signatoryModel.lastName === '') {
      this.errors.push({ type: SignatoryErrorType.LastNameEmpty, message: 'Last name is empty' })
    }
    if (this.signatoryModel.email === '') {
      this.errors.push({ type: SignatoryErrorType.EmailEmpty, message: 'Email is empty' })
    }
    if (this.signatoryModel.title === '') {
      this.errors.push({ type: SignatoryErrorType.TitleEmpty, message: 'Title is empty' })
    }

    if (this.totalEmployees === '') {
      this.errors.push({ type: SignatoryErrorType.totalEmployeesEmpty, message: 'Total Employees is empty' })
    }

    if (!this.signatoryModel.startDate) {
      this.startDateErrorMessage = 'Start Date is required';
      this.errors.push({ type: StartDateErrorType.StartDateEmpty, message: this.startDateErrorMessage });
    } else {
      const today = new Date();
      const todayIso = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      if (this.signatoryModel.startDate <= todayIso) {
        this.startDateErrorMessage = 'Start Date must be in the future';
        this.errors.push({ type: StartDateErrorType.StartDateNotFuture, message: this.startDateErrorMessage });
      } else {
        this.startDateErrorMessage = null;
      }
    }

    if (this.errors.length > 0) return false;
    else return true;
  }

  public getErrorByType(type: SignatoryErrorType | StartDateErrorType | string): FormError | undefined {
    return this.errors.find(e => e.type === (type as any));
  }
  public getModel() { return this.signatoryModel; }
  public setHotelID(hotelID: number) { this.signatoryModel.hotelId = hotelID; }
  public setTotalEmployees(totalEmployees: string) {
    this.totalEmployees = totalEmployees || '';
  }
  public getTotalEmployees(): string {
    return this.totalEmployees;
  }

  public refreshStartDateModel() {
    const iso = this.signatoryModel.startDate;
    this.originalStartDate = iso && iso.length >= 10 ? iso.substring(0, 10) : null;
    this.syncStartDateModelFromIso();
  }

  private syncStartDateModelFromIso() {
    const iso = this.signatoryModel.startDate;
    if (!iso || iso.length < 10) {
      this.startDateModel = null;
      return;
    }
    const y = parseInt(iso.substring(0, 4), 10);
    const m = parseInt(iso.substring(5, 7), 10);
    const d = parseInt(iso.substring(8, 10), 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) {
      this.startDateModel = null;
      return;
    }
    const jsdate = new Date(y, m - 1, d);
    this.startDateModel = {
      date: { year: y, month: m, day: d },
      jsdate: jsdate,
      formatted: '',
      epoc: Math.round(jsdate.getTime() / 1000)
    };
  }

  public hasStartDateChanged(): boolean {
    if (!this.originalStartDate) return false; // no prior date — setting it for the first time is not a change
    const sd = this.signatoryModel.startDate;
    const current = sd && sd.length >= 10 ? sd.substring(0, 10) : null;
    return current !== this.originalStartDate;
  }

  public revertStartDate() {
    this.signatoryModel.startDate = this.originalStartDate;
    this.syncStartDateModelFromIso();
    this.startDateErrorMessage = null;
    this.onModelChange();
  }

  public setStartDateLockState(reason: StartDateLockReason) {
    this.startDateLockReason = reason;
    this.isStartDateEditable = (reason !== 'FeinShared');
    this.startDateLockTooltip = this.computeLockTooltip();
  }

  private computeLockTooltip(): string {
    if (this.isStartDateEditable) return '';
    if (this.startDateLockReason === 'FeinShared') {
      return 'This entity has a shared EIN; the start date is set on the EIN-owning hotel.';
    }
    return '';
  }

  onModelChange() {
    this.onSignatorySaved.emit({ ...this.signatoryModel });
  }

  onTotalEmployeesValueChange(value: string) {
    this.totalEmployees = value || '';
    this.onTotalEmployeesChange.emit(this.totalEmployees);
  }

  onStartDateChanged(evt: IMyDateModel) {
    if (evt && evt.date && evt.date.year) {
      const m = evt.date.month.toString().padStart(2, '0');
      const d = evt.date.day.toString().padStart(2, '0');
      this.signatoryModel.startDate = `${evt.date.year}-${m}-${d}`;
    } else {
      this.signatoryModel.startDate = null;
    }
    this.startDateErrorMessage = null;
    this.onModelChange();
  }
}



// WEBPACK FOOTER //
// ./src/app/Subscription/Signatory/SignatoryForm.component.ts